import { Finding, SuburbGraph, growFrontier, runChecks } from '../rules/suburb-map-checks';
import {
  SubRegionAction,
  SubRegionFinding,
  growSubRegion,
  subRegionChecks,
} from '../rules/suburb-subregion-edits';
import {
  EditContext,
  EditMode,
  grewStatus,
  lassoStatus,
  planEdits,
  readOnlyStatus,
} from './map-editing';
import {
  EngineHooks,
  Gesture,
  MapEdit,
  MapMode,
  MapRow,
  MapSubRegion,
  SubRegionPick,
} from './map-types';
import { SuburbMapModel } from './suburb-map.model';

/** The region and sub-region checks' findings, with the graph to name what they found. */
export interface CheckResults {
  findings: Finding[];
  subRegionFindings: SubRegionFinding[];
  graph: SuburbGraph;
}

/**
 * What the editing tools do to the map (#256, #260): a click, a paint stroke, a lasso, Grow, Fill enclaves and the
 * panels' one-suburb buttons. Each is one action: the edits are applied to the model at once and reported through
 * onAction as one save and one undo step. The rules themselves are map-editing.ts and rules/.
 */
export class MapEditor {
  constructor(
    private map: SuburbMapModel,
    private hooks: EngineHooks
  ) {}

  /** Apply edits for these suburbs and report them as one action. Returns how many suburbs it touched. */
  act(
    codes: string[],
    gesture: Gesture,
    label: string,
    mode: MapMode = this.map.mode,
    sub: SubRegionPick = {}
  ): number {
    const before: MapRow[] = [];
    const edits = this.editCodes(codes, gesture, before, mode, sub);
    if (!edits.length) return 0;
    this.map.render();
    this.map.markChanged();
    this.hooks.onAction(edits, label, before);
    return new Set(edits.map((e) => e.salCode)).size;
  }

  /** The mode's edits to these suburbs, applied locally; the touched suburbs as they were go into `before`. */
  editCodes(
    codes: string[],
    gesture: Gesture,
    before: MapRow[] = [],
    mode: MapMode = this.map.mode,
    sub: SubRegionPick = {}
  ): MapEdit[] {
    if (mode === 'view') return [];
    if (!this.map.editable) {
      this.hooks.onStatus(readOnlyStatus(this.map.overview), true);
      return [];
    }
    const plan = planEdits(codes, gesture, this.context(mode, sub));
    for (const code of new Set(plan.edits.map((e) => e.salCode))) before.push(this.map.rowOf(code));
    for (const e of plan.edits) this.map.applyEdit(e);
    if (plan.status) this.hooks.onStatus(plan.status.message, plan.status.error);
    return plan.edits;
  }

  /** One suburb, one mode, from the panel's buttons: "Put in <region>" does only the region (no sub-region pick). */
  editOne(code: string, mode: EditMode, label: string): number {
    return this.act([code], 'click', label, mode, { subRegionId: null });
  }

  /** One suburb into or out of one sub-region, from the panels (Members ×, "Add to sub-region…"). */
  editMembership(
    code: string,
    subRegionId: string,
    action: SubRegionAction,
    label: string
  ): number {
    return this.act([code], 'sweep', label, 'subregion', { subRegionId, action });
  }

  /** What a closed lasso caught, in the current mode. */
  lasso(hits: string[]): void {
    if (!hits.length) return this.hooks.onStatus('The lasso caught nothing.');
    const n = this.act(hits, 'sweep', 'lasso');
    const alsoInto =
      this.map.mode === 'assign' ? (this.map.assignSubRegionRef?.name ?? null) : null;
    if (n) this.hooks.onStatus(lassoStatus(this.map.mode, this.map.subRegionAction, n, alsoInto));
  }

  /** Grow the active region by the unassigned suburbs bordering it (or, in Sub-region mode, the active sub-region). */
  async grow(): Promise<void> {
    const m = this.map;
    if (!m.editable) {
      this.editCodes([], 'sweep'); // says why
      return;
    }
    if (m.mode === 'subregion') return this.growSubRegion();
    const region = m.activeRegion;
    if (!region) return this.hooks.onStatus('Pick a region first.', true);
    const graph = await m.graph();
    const regionOf = new Map([...m.areas].map(([k, v]) => [k, v.regionId]));
    const frontier = growFrontier(graph, regionOf, region, m.visibleCodes());
    const name = m.regionById(region)?.name ?? region;
    if (!frontier.length) return this.hooks.onStatus(`Nothing unassigned borders ${name} here.`);
    // With a sub-region picked in Assign mode, the new suburbs join it too (same action, one undo step). In Erase or
    // View (Grow's button and G still work there) the picker is off screen, so Grow puts them in the region only.
    const sr = m.mode === 'assign' ? m.assignSubRegionRef : undefined;
    const n = this.act(frontier, 'sweep', `grow ${name}`, 'assign', {
      subRegionId: sr?.id ?? null,
    });
    if (n) this.hooks.onStatus(grewStatus(name, n, sr?.name ?? null));
  }

  /** Sub-region mode's Grow: the active sub-region's region suburbs that border it join it, as one action. */
  private async growSubRegion(): Promise<void> {
    const m = this.map;
    const sr = m.subRegionById(m.activeSubRegion);
    if (!sr) return this.hooks.onStatus('Pick a sub-region first.', true);
    const frontier = growSubRegion(await m.graph(), m.areas, sr, m.visibleCodes());
    if (!frontier.length)
      return this.hooks.onStatus(
        `No suburb of ${m.regionById(sr.regionId)?.name ?? 'its region'} borders ${sr.name} here.`
      );
    const n = this.act(frontier, 'sweep', `grow ${sr.name}`, 'subregion', { action: 'add' });
    if (n) this.hooks.onStatus(grewStatus(sr.name, n, null));
  }

  /** The region and sub-region checks over every assignment, with the graph to name what they found. */
  async checks(): Promise<CheckResults> {
    const m = this.map;
    const graph = await m.graph();
    const regionOf = new Map([...m.areas].map(([k, v]) => [k, v.regionId]));
    const regionState = new Map(m.regions.map((r) => [r.id, r.state]));
    return {
      findings: runChecks(graph, regionOf, regionState),
      subRegionFindings: subRegionChecks(graph, m.areas, m.subRegions),
      graph,
    };
  }

  /** Fill each enclave whose neighbours are all one region with that region, as one action. */
  fillEnclaves(findings: Finding[]): number {
    const m = this.map;
    if (!m.editable) {
      this.editCodes([], 'sweep'); // says why
      return 0;
    }
    const edits: MapEdit[] = [];
    const before: MapRow[] = [];
    for (const f of findings) {
      if (f.kind !== 'enclave' || !f.fix || m.areas.has(f.code)) continue;
      const e: MapEdit = { salCode: f.code, field: 'region', from: null, to: f.fix };
      before.push(m.rowOf(f.code));
      m.applyEdit(e);
      edits.push(e);
    }
    if (edits.length) {
      m.render();
      m.markChanged();
      this.hooks.onAction(edits, 'fill enclaves', before);
    }
    return edits.length;
  }

  private context(mode: EditMode, sub: SubRegionPick): EditContext {
    const m = this.map;
    return {
      mode,
      areas: m.areas,
      pages: m.pages,
      activeRegion: m.activeRegion,
      subRegion: this.subRegionFor(mode, sub) ?? null,
      subRegionAction: sub.action ?? m.subRegionAction,
      regionName: (id) => m.regionById(id)?.name ?? id,
      areaName: (code) => m.areaName(code),
    };
  }

  /** Sub-region mode edits the active sub-region; Assign joins its picked one (an explicit null in `sub` means none). */
  private subRegionFor(mode: EditMode, sub: SubRegionPick): MapSubRegion | undefined {
    const m = this.map;
    if (mode === 'subregion') return m.subRegionById(sub.subRegionId ?? m.activeSubRegion);
    if (mode === 'assign')
      return m.subRegionById(sub.subRegionId !== undefined ? sub.subRegionId : m.assignSubRegion);
    return undefined;
  }
}
